/**
 * Standard API error structure
 */
export interface ApiError {
  code: string;
  message: string;
  details?: any;
  requestId?: string;
  serviceName?: string;
  status: number;
}

/**
 * Common error codes used across the application
 */
export enum ErrorCode {
  // Authentication errors
  UNAUTHORIZED = 'UNAUTHORIZED',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  TOKEN_INVALID = 'TOKEN_INVALID',
  
  // Authorization errors
  FORBIDDEN = 'FORBIDDEN',
  INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS',
  
  // Resource errors
  RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND',
  RESOURCE_ALREADY_EXISTS = 'RESOURCE_ALREADY_EXISTS',
  
  // Validation errors
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  INVALID_REQUEST = 'INVALID_REQUEST',
  
  // Server errors
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  
  // Network errors
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  
  // General errors
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  BAD_REQUEST = 'BAD_REQUEST'
}

/**
 * Helper to determine if an object is an ApiError
 */
export function isApiError(error: any): error is ApiError {
  return (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    'message' in error &&
    'status' in error
  );
}

/**
 * Get user-friendly error message from API error
 */
export function getUserFriendlyErrorMessage(error: any): string {
  if (!error) {
    return 'An unknown error occurred';
  }
  
  // If it's an ApiError object
  if (isApiError(error)) {
    // Handle specific error codes with user-friendly messages
    switch (error.code) {
      case ErrorCode.UNAUTHORIZED:
        return 'You need to be logged in to perform this action';
      
      case ErrorCode.INVALID_CREDENTIALS:
        return 'The email or password you entered is incorrect';
      
      case ErrorCode.TOKEN_EXPIRED:
        return 'Your session has expired. Please log in again';
      
      case ErrorCode.FORBIDDEN:
      case ErrorCode.INSUFFICIENT_PERMISSIONS:
        return 'You do not have permission to perform this action';
      
      case ErrorCode.RESOURCE_NOT_FOUND:
        return 'The requested resource could not be found';
      
      case ErrorCode.VALIDATION_ERROR:
        return error.message || 'The information you provided is invalid';
      
      case ErrorCode.NETWORK_ERROR:
        return 'Unable to connect to the server. Please check your internet connection';
      
      case ErrorCode.SERVICE_UNAVAILABLE:
        return 'The service is currently unavailable. Please try again later';
      
      default:
        // If no specific handling, use the message from the API
        return error.message || 'An error occurred';
    }
  }
  
  // For string errors
  if (typeof error === 'string') {
    return error;
  }
  
  // For Error objects
  if (error instanceof Error) {
    return error.message;
  }
  
  // For unexpected error formats
  return 'An unexpected error occurred';
}

/**
 * Determine if an error is a network error
 */
export function isNetworkError(error: any): boolean {
  if (isApiError(error)) {
    return error.code === ErrorCode.NETWORK_ERROR || 
           error.code === ErrorCode.TIMEOUT_ERROR;
  }
  
  // Check if it's an Axios network error
  if (error && error.isAxiosError && !error.response) {
    return true;
  }
  
  return false;
}

/**
 * Determine if an error is an authentication error
 */
export function isAuthError(error: any): boolean {
  if (isApiError(error)) {
    return error.code === ErrorCode.UNAUTHORIZED || 
           error.code === ErrorCode.TOKEN_EXPIRED ||
           error.code === ErrorCode.TOKEN_INVALID ||
           error.status === 401;
  }
  
  // Check if it's an Axios auth error
  if (error && error.isAxiosError && error.response?.status === 401) {
    return true;
  }
  
  return false;
}

/**
 * Safe error serialization that prevents [object Object] display
 */
export function safeSerializeError(error: any): string {
  if (!error) {
    return 'Unknown error (null/undefined)';
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  if (error instanceof Error) {
    return error.message || error.name || 'Error object with no message';
  }
  
  if (isApiError(error)) {
    return `${error.code}: ${error.message}`;
  }
  
  // For objects, try to extract meaningful information
  if (typeof error === 'object') {
    if (error.message) {
      return String(error.message);
    }
    if (error.error?.message) {
      return String(error.error.message);
    }
    if (error.toString && typeof error.toString === 'function') {
      const str = error.toString();
      if (str !== '[object Object]') {
        return str;
      }
    }
    
    // Try to extract key properties
    try {
      const keys = Object.keys(error);
      if (keys.length > 0) {
        return `Error object with properties: ${keys.join(', ')}`;
      }
    } catch (e) {
      // Ignore
    }
    
    return 'Error object (unable to serialize)';
  }
  
  return String(error);
}

/**
 * Enhanced error reporting that integrates with existing error boundary
 */
export function reportError(error: any, context?: string, additionalInfo?: Record<string, any>): void {
  const safeMessage = safeSerializeError(error);
  const errorContext = context || 'RuntimeError';
  
  // Log the error using existing logError function
  logError(error, errorContext);
  
  // Create a custom error event for the ErrorBoundary to catch
  const errorEvent = new CustomEvent('app-error', {
    detail: {
      error: error instanceof Error ? error : new Error(safeMessage),
      context: errorContext,
      additionalInfo,
      timestamp: new Date().toISOString()
    }
  });
  
  // Dispatch the event so ErrorBoundary or other listeners can handle it
  window.dispatchEvent(errorEvent);
}

/**
 * Centralized error logging
 */
export function logError(error: any, context?: string): void {
  const prefix = `[${context || 'Error'}]`;
  const safeMessage = safeSerializeError(error);

  // In development, log more details as strings
  if (process.env.NODE_ENV === 'development') {
    if (isApiError(error)) {
      console.error(`${prefix} API Error: ${error.code} - ${error.message}`, error);
    } else if (error instanceof Error) {
      console.error(`${prefix} Error: ${error.message}`, error.stack);
    } else if (typeof error === 'object' && error !== null) {
      console.error(`${prefix} Object Error: ${safeMessage}`, error);
    } else {
      console.error(`${prefix} ${safeMessage}`);
    }
    return;
  }

  // In production, log structured error info but avoid sensitive data
  if (isApiError(error)) {
    console.error(`[${context || 'Error'}] ${error.code}: ${error.message}`, {
      code: error.code,
      status: error.status,
      requestId: error.requestId,
      service: error.serviceName
    });
  } else if (error instanceof Error) {
    console.error(`[${context || 'Error'}] ${error.name}: ${error.message}`);
  } else {
    console.error(`[${context || 'Error'}] ${safeMessage}`);
  }
  
  // Here you could add integration with error monitoring services like Sentry
  // if (window.Sentry) {
  //   window.Sentry.captureException(error);
  // }
}
